import { View } from "@novorender/api";
import { ReadonlyQuat, ReadonlyVec3, vec2, vec3 } from "gl-matrix";

import { getAnchorPos2d } from "features/clippingPlanes/useClippingPlaneActions";
import { getCameraDir } from "features/engine2D/utils";
import { RenderState } from "features/render";

type SectionPlane = RenderState["clipping"]["planes"][number];
type CanvasRect = Pick<DOMRect, "left" | "top" | "width" | "height">;

/** Returns screen-space move direction and distance-based multiplier for dragging a section plane. */
export function getScreenSpaceMoveDirAndMult(
    view: View,
    anchorPos: vec3,
    normal: vec3,
    outDir2d?: vec2,
    scratchVec3?: vec3,
): { dir2d: vec2; mult: number } | null {
    const anchorToCameraDist = vec3.dist(anchorPos, view.renderState.camera.position);
    const offset = Math.max(1, anchorToCameraDist / 10);
    const refPosWorld = scratchVec3 ?? vec3.create();
    vec3.scaleAndAdd(refPosWorld, anchorPos, normal, -offset);
    const screenPoints = view.convert.worldSpaceToScreenSpace([refPosWorld, anchorPos], { round: false });
    const refScreen = screenPoints[0];
    const anchorScreen = screenPoints[1];
    if (!refScreen || !anchorScreen) {
        return null;
    }
    const dir2d = outDir2d ?? vec2.create();
    vec2.sub(dir2d, refScreen, anchorScreen);
    const len = vec2.len(dir2d);
    if (len <= 1e-6) {
        return null;
    }
    const mult = offset / len;
    vec2.scale(dir2d, dir2d, 1 / len);
    return { dir2d, mult };
}

export function getSectionPlanePoints(view: View, planes: RenderState["clipping"]["planes"]) {
    const sectionPlanes = planes.map((plane, index) => ({ plane, index })).filter(({ plane }) => plane?.anchorPos);
    const points = getAnchorPos2d(
        view,
        sectionPlanes.map(({ plane }) => plane.anchorPos!),
    );
    return sectionPlanes.map(({ plane, index }, i) => ({
        plane,
        index,
        point: points[i],
    }));
}

/** Returns the rotation angle to align the double-arrow with the plane's movement direction on screen. */
export function getSectionPlaneArrowAngle(view: View, plane: SectionPlane): number {
    if (!plane?.anchorPos) {
        return 0;
    }

    const normal = vec3.fromValues(plane.normalOffset[0], plane.normalOffset[1], plane.normalOffset[2]);
    const cameraDir = getCameraDir(view.renderState.camera.rotation);
    const isFacingNormal = Math.abs(vec3.dot(normal, cameraDir)) > 0.999;
    if (isFacingNormal) {
        return 0;
    }

    const anchorScreen = view.convert.worldSpaceToScreenSpace([plane.anchorPos], { round: false })[0];
    const offsetPos = vec3.scaleAndAdd(vec3.create(), plane.anchorPos, normal, 1);
    const offsetScreen = view.convert.worldSpaceToScreenSpace([offsetPos], { round: false })[0];

    if (!anchorScreen || !offsetScreen) {
        return 0;
    }

    const dir2d = vec2.sub(vec2.create(), offsetScreen, anchorScreen);
    vec2.normalize(dir2d, dir2d);

    return (Math.atan2(-dir2d[0], dir2d[1]) * 180) / Math.PI;
}

export function buildPerspectiveRayDir(
    cameraRotation: ReadonlyQuat,
    cameraDir: ReadonlyVec3,
    fovDeg: number,
    canvasRect: CanvasRect,
): (clientX: number, clientY: number, out: vec3) => void {
    const tanHalfFov = Math.tan((fovDeg * Math.PI) / 360);
    const aspect = canvasRect.width / canvasRect.height;
    const cameraRight = vec3.transformQuat(vec3.create(), vec3.fromValues(1, 0, 0), cameraRotation);
    const cameraUp = vec3.transformQuat(vec3.create(), vec3.fromValues(0, 1, 0), cameraRotation);

    return (clientX: number, clientY: number, out: vec3) => {
        const ndcX = (2 * (clientX - canvasRect.left)) / canvasRect.width - 1;
        const ndcY = -((2 * (clientY - canvasRect.top)) / canvasRect.height - 1);
        vec3.copy(out, cameraDir);
        vec3.scaleAndAdd(out, out, cameraRight, ndcX * aspect * tanHalfFov);
        vec3.scaleAndAdd(out, out, cameraUp, ndcY * tanHalfFov);
        vec3.normalize(out, out);
    };
}
